import type { FilterRuleModel } from '@/shared/generated'

import React, { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { all, any, pipe, prop } from 'lodash/fp'
import { ArrowUpRightIcon, EllipsisVerticalIcon } from 'lucide-react'
import QueryString from 'qs'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import {
	createChoices,
	isFilterbaleField,
	isPreproField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import {
	useBrandsQuery,
	useCategoriesQuery,
	useSettingsSchemasQuery,
} from '@/entities/core'
import { useStopListSuggestsQuery } from '@/entities/optimization-statistic'

import { ActionsDropdown } from '@/shared/components/ActionsDropdown'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { isValidRule } from '@/shared/components/filter/utils'
import RHFSettingsWidget from '@/shared/components/HookForm/RHFSettingsWidget'
import RHFStoplistCustomRules from '@/shared/components/HookForm/RHFStoplistCustomRules'
import { Operators } from '@/shared/components/query-builder'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import { Loader } from '@/shared/components/ui/loader'
import { ModalClose } from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { HiddenOnContext } from '@/shared/generated'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'
import { encodeComplexQuery } from '@/shared/tools/location'
import { dotCase } from '@/shared/tools/utils'

export const StopListFieldsCard = ({
	prefix,
	optimizationGroupId,
	scenarioId,
	skuRules,
	actions,
}: {
	prefix?: string
	optimizationGroupId?: string
	scenarioId?: string
	skuRules?: FilterRuleModel[]
	actions: any[]
}) => {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:stop_list'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const fieldsQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment, {
		select: (data) => data.filter(isPreproField).filter(isFilterbaleField),
	})
	const brandsQuery = useBrandsQuery(
		optimizationGroupId ? { optimization_group_id: optimizationGroupId } : {}
	)
	const categoriesQuery = useCategoriesQuery({
		optimization_group_id: optimizationGroupId!,
	})
	const fields = useMemo(
		() => fieldsQuery.data?.map((schema) => toAdaptedSchema(schema, t)) || [],
		[fieldsQuery.data, t]
	)
	const entities = useMemo(
		() => ({
			category: categoriesQuery.data || [],
			brand: brandsQuery.data || [],
		}),
		[categoriesQuery.data, brandsQuery.data]
	)
	const dataChoices = useMemo(() => {
		const schema = fieldsQuery.data || []

		return createChoices({
			schema,
			entities,
		})
	}, [entities, fieldsQuery.data])
	const isLoading = any(prop('isLoading'), [
		fieldsQuery,
		brandsQuery,
		categoriesQuery,
		schemaQuery,
	])
	if (isLoading) {
		return (
			<div className='flex size-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-3'>
			<Card data-testid='og-settings-stop-list-custom-rules-panel'>
				<CardHeader>
					<CardTitle>{t('stop_list.default_rules.title')}</CardTitle>
					<CardDescription>
						{t('stop_list.default_rules.subtitle')}
					</CardDescription>
					<CardAction>
						<div className='flex items-center gap-2'>
							{!!scenarioId && (
								<LockedProductsCount
									optimizationGroupId={optimizationGroupId}
									scenarioId={scenarioId}
									prefix={prefix}
								/>
							)}
							<ActionsDropdown actions={actions}>
								<Button
									data-testid='scenario-actions-popover-trigger'
									variant='ghost'
									size='icon-sm'
								>
									<EllipsisVerticalIcon />
								</Button>
							</ActionsDropdown>
						</div>
					</CardAction>
				</CardHeader>
				<CardContent>
					<ErrorBoundary>
						<RHFSettingsWidget
							name={dotCase([prefix, 'stop_list', 'default_rules'])}
							schema={schemaQuery.data.properties.default_rules}
						/>
					</ErrorBoundary>
				</CardContent>
				<CardHeader>
					<CardTitle>{t('stop_list.custom_rules.title')}</CardTitle>
					<CardDescription>
						{t('stop_list.custom_rules.subtitle')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ErrorBoundary>
						<RHFStoplistCustomRules
							name={dotCase([prefix, 'stop_list', 'custom_rules'])}
							fields={fields}
							dataChoices={dataChoices}
						/>
					</ErrorBoundary>
				</CardContent>
			</Card>
		</div>
	)
}

const LockedProductsCount = ({
	optimizationGroupId,
	scenarioId,
	prefix,
}: {
	optimizationGroupId?: string
	scenarioId: string
	prefix?: string
}) => {
	const { t } = useTranslation()

	const { isDirty } = useFormState()
	const settings = useWatch({
		name: dotCase([prefix, 'stop_list']),
	})
	const link = `/p/og/${optimizationGroupId}/products${QueryString.stringify(
		{
			qf: encodeComplexQuery({
				filters: [
					{
						name: 'is_locked',
						operation: Operators.BL,
						value: 1,
					},
				],
			}),
		},
		{ addQueryPrefix: true }
	)}`
	const [debounceSettings] = useDebounceValue(settings, 2000)
	const predictedStopListCountsQuery = useStopListSuggestsQuery(
		{ scenarioId },
		{
			settings: {
				stop_list: debounceSettings,
			},
		},
		{
			enabled: Boolean(
				!!scenarioId &&
				!!debounceSettings?.custom_rules &&
				all(isValidRule, debounceSettings?.custom_rules)
			),
		}
	)
	const isLoading = predictedStopListCountsQuery.isLoading
	const count = predictedStopListCountsQuery.data?.product_count ?? 0
	if (isLoading) {
		return (
			<span
				className='flex items-center gap-1 text-xs text-muted-foreground'
				data-testid='stop-list-locked-products-count-loader'
			>
				<Spinner className='size-3' data-icon='inline-start' />
				{t('app.loading', { defaultValue: 'Loading...' })}
			</span>
		)
	}
	if (isDirty) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge data-testid='stop-list-locked-products-count-badge'>
						{t('stop_list.should_be_locked_counts', {
							count,
							defaultValue: `${count} SKUs should be locked`,
						})}
					</Badge>
				</TooltipTrigger>
				<TooltipContent>
					{t('stop_list.should_be_locked_counts.tooltip', {
						defaultValue: 'Suggested count is based on the rules.',
					})}
				</TooltipContent>
			</Tooltip>
		)
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<ModalClose asChild>
					<Button asChild size='xs' variant='link'>
						<Link data-testid='stop-list-locked-products-count-link' to={link}>
							{t('stop_list.locked_counts', {
								count,
								defaultValue: `${count} SKUs locked`,
							})}
							<ArrowUpRightIcon data-icon='inline-end' />
						</Link>
					</Button>
				</ModalClose>
			</TooltipTrigger>
			<TooltipContent>
				{t('stop_list.locked_counts.tooltip', {
					defaultValue: 'Click to see locked products',
				})}
			</TooltipContent>
		</Tooltip>
	)
}
